import * as Yup from 'yup';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import type { OptionType } from '@withjoy/joykit';
import type { FormikHelpers } from 'formik';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import type { TranslateFunction } from '@locales/generated';
import type { ShippingAddressFragment } from '@graphql/aliases';
import { useGetRegistryStaticDataQuery } from '@graphql/queries/registry.graphql.generated';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useAddressDialogContext } from '../../AddressDialog.provider';
import type { AddressDialogFields } from '../../types';
import { AddressUsedEnum } from '../../types';

// Got this from styled-system
const isNumber = (n: unknown): n is number => typeof n === 'number' && !isNaN(n);

const NATIONAL_COUNTRY_CODE = 'USA';

interface AddressDialogControllerProps {
  registryShippingAddress: ShippingAddressFragment | null;
  countryOtherByCode: TranslateFunction;
}

export const useAddressFormController = ({ registryShippingAddress, countryOtherByCode }: AddressDialogControllerProps) => {
  const enableAddressVerificationModal = useFeatureValue('enableAddressVerificationModal');

  const {
    mutationLoading,
    onClose: onCloseAddressDialog,
    onSaveFormAddress,
    setAddressUsed,
    onGoToNextStep,
    setStep,
    setSuggestedAddress,
    suggestedAddress,
    onCallSuggestionApi,
    onDisableAddressSuggestions,
  } = useAddressDialogContext();
  const { t: translateForm } = useTranslation('generalFormValidation');
  const { data, loading: isCountryLoading } = useGetRegistryStaticDataQuery();

  const { countryList, topRankedCountryList } = useMemo(() => {
    const orderedCountries: Record<string, string> = {};
    (data?.getRegistryStaticData?.countries?.ordered || []).map(country => {
      orderedCountries[country.code] = country.name;
    });

    const topRankedCountries: Record<string, string> = {};
    (data?.getRegistryStaticData?.countries?.topRanked || []).map(country => {
      topRankedCountries[country.code] = country.name;
    });

    const orderedCountryList: OptionType[] =
      Object.keys(orderedCountries).map(cc => {
        return {
          label: orderedCountries[cc],
          value: cc,
        };
      }) || [];

    // the :F suffix is used to indicate which countries are in top ranked
    const topRankedCountryList: OptionType[] =
      Object.keys(topRankedCountries).map(cc => {
        return {
          label: topRankedCountries[cc],
          value: cc + ':F',
        };
      }) || [];

    const countryList: OptionType[] = topRankedCountryList
      .concat([
        {
          label: '---------------',
          value: '--',
          disabled: true,
        },
      ])
      .concat(orderedCountryList)
      .concat([
        {
          label: countryOtherByCode(),
          value: '',
        },
      ]);
    return { countryList, topRankedCountryList };
  }, [countryOtherByCode, data]);

  const [loadingNextStep, setLoadingNextStep] = useState<boolean>(false);
  const [isNationalAddress, setIsNationalAddress] = useState<boolean>(!registryShippingAddress?.countryCode || registryShippingAddress?.countryCode === NATIONAL_COUNTRY_CODE);

  const [countryDropdownValue, setCountryDropdownValue] = useState<OptionType>({
    label: registryShippingAddress?.country || '',
    value: registryShippingAddress?.countryCode || '',
  });
  const [otherCountryFieldValue, setOtherCountryFieldValue] = useState<string>(registryShippingAddress?.countryCode || '');

  const dialogRef = useRef<HTMLDivElement>(null);
  const [clearAddressPressed, setClearAddressPressed] = useState<boolean>(false);

  const getTopRankedCountryValue = useCallback(() => topRankedCountryList[0]?.value.split(':')[0] || '', [topRankedCountryList]);

  const selectCountryOption = useCallback(() => {
    const country: string = registryShippingAddress?.country || topRankedCountryList[0]?.label || '';
    const countryCode: string = registryShippingAddress?.countryCode || getTopRankedCountryValue();

    if (registryShippingAddress && !!registryShippingAddress.country && !registryShippingAddress.countryCode) {
      // Other value has been selected
      setCountryDropdownValue({
        label: countryOtherByCode(),
        value: '',
      });
      setOtherCountryFieldValue(country);
      return;
    }

    setCountryDropdownValue({
      label: country,
      value: countryCode,
    });
    setOtherCountryFieldValue(countryCode);
  }, [topRankedCountryList, registryShippingAddress, countryOtherByCode, getTopRankedCountryValue]);

  const handleSubmit = useCallback(
    async (shippingAddressFields: AddressDialogFields, actions: FormikHelpers<AddressDialogFields>) => {
      await onSaveFormAddress(shippingAddressFields);
      // `saveAddress` should handle submitting the form + closing the dialog
    },
    [onSaveFormAddress],
  );

  const formik = useFormik<AddressDialogFields>({
    enableReinitialize: true,
    initialValues: {
      name: registryShippingAddress?.name || '',
      phone: registryShippingAddress?.phone || '',
      address1: registryShippingAddress?.address1 || '',
      address2: registryShippingAddress?.address2 || '',
      city: registryShippingAddress?.city || '',
      state: registryShippingAddress?.state || '',
      postalCode: registryShippingAddress?.postalCode || '',
      country: registryShippingAddress?.country || topRankedCountryList[0]?.label || '',
      countryCode: registryShippingAddress?.countryCode || getTopRankedCountryValue(),
      validated: false,
    },
    onSubmit: handleSubmit,
    validationSchema: Yup.object<AddressDialogFields>({
      name: Yup.string().required(translateForm('required')),
      phone: Yup.string(),
      address1: Yup.string().required(translateForm('required')),
      address2: Yup.string(),
      city: Yup.string().required(translateForm('required')),
      state: Yup.string().required(translateForm('required')),
      postalCode: Yup.string().required(translateForm('required')),
      country: Yup.string().required(translateForm('required')),
      validated: Yup.boolean(),
    }),
  });

  const clearAddressFields = useCallback(() => {
    setClearAddressPressed(true);

    formik.setFieldValue('phone', '');
    formik.setFieldValue('address2', '');
    formik.setFieldValue('city', '');
    formik.setFieldValue('state', '');
    formik.setFieldValue('postalCode', '');

    // setTimeout func is used to let handleAddressInputChange from AddressAutocompleteField component be executed before setClearAddressPressed
    formik.setTouched({}, false);
    setTimeout(() => {
      setClearAddressPressed(false);
    }, 0);
  }, [formik]);

  const handleCountryDropdownChange = useCallback(
    (option: OptionType | null): void => {
      if (!option) {
        return;
      }

      // removing the :F from the value.
      const countryOption = { label: option.label || '', value: option.value.split(':')[0] || '' };

      setCountryDropdownValue(countryOption);
      setOtherCountryFieldValue(countryOption.value);
      setIsNationalAddress(countryOption.value === NATIONAL_COUNTRY_CODE);

      setSuggestedAddress(null);

      if (countryOption.value === '') {
        // Other value has been selected
        formik.setFieldValue('country', '');
        formik.setFieldValue('countryCode', null);
      } else {
        formik.setFieldValue('country', countryOption.label);
        formik.setFieldValue('countryCode', countryOption.value);
      }
      clearAddressFields();
    },
    [formik, clearAddressFields, setSuggestedAddress],
  );

  const handleOtherCountryFieldChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    event => {
      const country: string = event.target.value;

      setOtherCountryFieldValue(country);
      formik.setFieldValue('country', country);
      formik.setFieldValue('countryCode', null);
    },
    [formik],
  );

  const handlePostalCodeFieldChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    if (!isNationalAddress) {
      formik.handleChange(event);
      return;
    }

    const value = event.target.value;
    const charParsed = parseInt(value.charAt(value.length - 1));
    if (value === '' || (value.length <= 5 && isNumber(charParsed))) {
      formik.handleChange(event);
    }
  };

  const handleSave = useCallback(() => {
    !formik.isSubmitting && formik.submitForm();
  }, [formik]);

  const handleClearAddress = useCallback(() => {
    const country: string = topRankedCountryList[0]?.label || '';
    const countryCode: string = getTopRankedCountryValue();

    setIsNationalAddress(countryCode === NATIONAL_COUNTRY_CODE);

    setCountryDropdownValue({
      label: country,
      value: countryCode,
    });
    setOtherCountryFieldValue(countryCode);

    formik.setFieldValue('country', country);
    formik.setFieldValue('countryCode', countryCode);
    clearAddressFields();
  }, [clearAddressFields, formik, topRankedCountryList, getTopRankedCountryValue]);

  const handleNextStep = useCallback(async () => {
    const getFakeAddress = () => {
      return {
        name: formik.values.name,
        phone: formik.values.phone,
        address1: 'fake address1',
        address2: isNationalAddress ? 'fake address2' : '',
        postalCode: 'fake postalCode',
        city: formik.values.city,
        state: isNationalAddress ? formik.values.state : '',
        country: formik.values.country,
        countryCode: formik.values.countryCode || '',
        validated: false,
      };
    };

    const isAddressTheSameThanFormAddress = (address: ShippingAddressFragment | null) => {
      if (!address) {
        return false;
      }
      return (
        address.address1 === formik.values.address1 &&
        address.address2 === formik.values.address2 &&
        address.city === formik.values.city &&
        address.state === formik.values.state &&
        address.postalCode === formik.values.postalCode
      );
    };

    //if feature flag is disabled or the selected Address is the same than the form, then we save the direction directly
    const sameAddress = isAddressTheSameThanFormAddress(suggestedAddress);
    if (!enableAddressVerificationModal || sameAddress) {
      setLoadingNextStep(false);
      // when we remove the feature flag, the validated value will be true
      if (!sameAddress) {
        setAddressUsed(AddressUsedEnum.SUGGESTED_MODIFIED);
      }
      formik.setFieldValue('validated', enableAddressVerificationModal);
      !formik.isSubmitting && handleSave();
      return;
    }
    formik.setFieldValue('validated', false);
    //Before to go to the next screen, we can check if the suggested address and the form address are the same.
    setLoadingNextStep(!formik.isSubmitting);
    // const address = formik.values.address1;

    // if couple doesn't modify the form, then the second step is not needed.
    const addressAux = getFakeAddress();
    if (addressAux) {
      setSuggestedAddress(addressAux);
    }

    // if it doesn't find a suggestion or the suggestion is the same, then we store directly
    if (!addressAux || isAddressTheSameThanFormAddress(addressAux)) {
      formik.setFieldValue('validated', true);
      !formik.isSubmitting && handleSave();
    } else {
      //go to next step
      onGoToNextStep({
        name: formik.values.name,
        phone: formik.values.phone,
        address1: formik.values.address1,
        address2: formik.values.address2,
        city: formik.values.city,
        state: formik.values.state,
        postalCode: formik.values.postalCode,
        country: formik.values.country,
        countryCode: formik.values.countryCode || '',
        validated: false,
      });
      formik.isValid && setStep(2);
      dialogRef?.current?.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'smooth' });
    }

    setLoadingNextStep(false);
  }, [suggestedAddress, enableAddressVerificationModal, formik, isNationalAddress, handleSave, setAddressUsed, setSuggestedAddress, onGoToNextStep, setStep]);

  const { errors } = formik || {};
  const nameError = formik.touched.name && !!errors.name ? formik.errors.name : undefined;
  const phoneError = formik.touched.phone && !!errors.address2 ? formik.errors.address2 : undefined;
  const address2Error = formik.touched.address2 && !!errors.address2 ? formik.errors.address2 : undefined;
  const stateError = formik.touched.state && !!errors.state ? formik.errors.state : undefined;
  const cityError = formik.touched.city && !!errors.city ? formik.errors.city : undefined;
  const postalCodeError = formik.touched.postalCode && !!errors.postalCode ? formik.errors.postalCode : undefined;
  const countryError = formik.touched.country && !!errors.country ? formik.errors.country : undefined;

  useEffect(() => {
    if (!isCountryLoading) {
      selectCountryOption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCountryLoading]);

  const onClose = () => {
    onCloseAddressDialog();
    setAddressUsed(AddressUsedEnum.CUSTOM);
  };

  return {
    formik,
    loadingNextStep,
    isCountryLoading,
    isOtherCountryFieldVisible: countryDropdownValue.value === '',
    countryList,
    countryDropdownValue,
    otherCountryFieldValue,
    handleCountryDropdownChange,
    handleOtherCountryFieldChange,
    handlePostalCodeFieldChange,
    handleNextStep,
    handleClearAddress,
    dialogRef,
    nameError,
    phoneError,
    address2Error,
    stateError,
    cityError,
    postalCodeError,
    countryError,
    loading: loadingNextStep || mutationLoading,
    onClose,
    isNationalAddress,
    clearAddressPressed,
    setSuggestedAddress,
    onCallSuggestionApi,
    setAddressUsed,
    onDisableAddressSuggestions,
  };
};
